import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLatest } from 'react-use'
import { DrawControllerWrapper } from '@scene'
import { DoubleSide, Euler, Plane as ThreePlane, Vector3 } from 'three'
import { useTheme, ThemeProvider } from '@mui/material/styles'
import {
  ScalingIndicator,
  TransientDrawState,
  sceneConfig,
  useCameraStore,
} from '@modugen/ui-toolkit'
import { Line } from '@react-three/drei'
import { ThreeEvent, useThree } from '@react-three/fiber'
import { subscribeClipPlaneCreateEvent, unsubscribeClipPlaneCreateEvent } from '@ui/events'
import type { ClipPlaneCreateEvent, ClipPlaneRenderMode, ClipPlaneTarget } from '@ui/events'
import { useTriggerContextMenu } from '@ui/hooks'
import { SceneContextMenuActionRenderProps } from '@ui/scene/Context/types'
import { useAppStore } from '@stores'
import { useControlStore, useSceneStore } from '@editorStores'
import {
  useClickActionEnabled,
  useMouseMovedSinceMouseDown,
  useSceneContextMenuAction,
  useSelectionMode,
} from '@editorHooks'
import BracketDistanceField from './BracketDistanceField'
import { CLIP_PLANE_POINT_SELECTION_KEY } from './constants'
import {
  ClipPlaneRectangle,
  createClipPlaneRectangle,
  getClipPlaneCenterOnPlane,
  makeClipPlaneOutlinePoints,
  makeClipPlaneRotation,
} from './utils'

const DEFAULT_BRACKET_MODE_DISTANCE = 1

type ClipPlaneMode = 'half-space' | 'bracket'

interface ClipPlaneConfig {
  id: string
  normal: Vector3
  point: Vector3
  offset: number
  isInverted: boolean
  rectangle: ClipPlaneRectangle
  target: ClipPlaneTarget
  linkGroupId?: string
  renderMode: ClipPlaneRenderMode
  mode: ClipPlaneMode
  bracketDistance: number
}

interface ResolvedClipPlane extends ClipPlaneConfig {
  clippingPlanes: ThreePlane[]
  controllerPlane: ThreePlane
  controllerPosition: Vector3
  rotation: Euler
  outlinePoints: Vector3[]
}

interface DragState {
  planeId: string
  dragPlane: ThreePlane
  dragAxis: Vector3
  offsetDirection: number
  pointerId: number
  startOffset: number
  linkedStartOffsets: Record<string, number>
  startPoint: Vector3
}

interface PointerCaptureTarget extends EventTarget {
  setPointerCapture: (pointerId: number) => void
  releasePointerCapture: (pointerId: number) => void
}

interface SceneControls {
  enabled: boolean
}

interface ClipPlaneActionPayload {
  planeId?: string
  normal?: unknown
  point?: unknown
}

const toVector3 = (value: unknown): Vector3 | null => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'x' in value &&
    'y' in value &&
    'z' in value &&
    typeof value.x === 'number' &&
    typeof value.y === 'number' &&
    typeof value.z === 'number'
  ) {
    return new Vector3(value.x, value.y, value.z)
  }

  return null
}

const toClipPlaneActionPayload = (value: unknown): ClipPlaneActionPayload | null => {
  if (typeof value === 'object' && value !== null) {
    return value as ClipPlaneActionPayload
  }

  return null
}

const DEFAULT_CLIP_PLANE_RENDER_MODE: ClipPlaneRenderMode = 'solid-plane'

const makeDragPlaneNormal = (dragAxis: Vector3, cameraDirection: Vector3) => {
  const normalizedDragAxis = dragAxis.clone().normalize()
  const dragPlaneNormal = cameraDirection
    .clone()
    .sub(normalizedDragAxis.multiplyScalar(cameraDirection.dot(normalizedDragAxis)))

  if (dragPlaneNormal.lengthSq() > 1e-6) {
    return dragPlaneNormal.normalize()
  }

  const fallbackReference =
    Math.abs(normalizedDragAxis.z) < 0.9 ? new Vector3(0, 0, 1) : new Vector3(0, 1, 0)
  const fallbackNormal = fallbackReference
    .clone()
    .sub(normalizedDragAxis.multiplyScalar(fallbackReference.dot(normalizedDragAxis)))

  if (fallbackNormal.lengthSq() > 1e-6) {
    return fallbackNormal.normalize()
  }

  return new Vector3(0, 1, 0)
}

const ClipController = (): ReactElement => {
  const theme = useTheme()
  const camera = useThree(state => state.camera)
  const controls = useThree(state => state.controls as unknown as SceneControls | undefined)
  const gl = useThree(state => state.gl)
  const scene = useThree(state => state.scene)
  const clipPlaneSizeFromScene = useAppStore(state => state.clipPlaneSizeFromScene)
  const applyClipPlanes = useControlStore(state => state.applyClipPlanes)
  const clipPlanesInteractive = useControlStore(state => state.clipPlanesInteractive)
  const setApplyClipPlanes = useControlStore(state => state.setApplyClipPlanes)
  const pendingClipPlanePoints = useControlStore(state => state.pendingClipPlanePoints)
  const setPendingClipPlanePoints = useControlStore(state => state.setPendingClipPlanePoints)
  const clipPlaneResetVersion = useControlStore(state => state.clipPlaneResetVersion)
  const { selectionModeKey, unsetSelectionMode } = useSelectionMode()
  const { openContextMenu } = useTriggerContextMenu()
  const { mouseMoved } = useMouseMovedSinceMouseDown()
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const clipPlaneDrawEnabled = useClickActionEnabled({ disableOnCoordinateInputFocused: false })
  const setGltfClipPlanes = useSceneStore(state => state.setGltfClipPlanes)
  const setClipPlaneIds = useSceneStore(state => state.setClipPlaneIds)

  const [clipPlanes, setClipPlanes] = useState<ClipPlaneConfig[]>([])
  const clipPlanesRef = useLatest(clipPlanes)
  const isPickingClipPlanePoints = selectionModeKey === CLIP_PLANE_POINT_SELECTION_KEY

  const dragStateRef = useRef<DragState | null>(null)
  const nextPlaneIdRef = useRef(1)
  const pendingClipPlanePointsRef = useLatest(pendingClipPlanePoints)
  const previousClippingPlanesRef = useRef<ThreePlane[] | null>(null)
  const previousLocalClippingEnabledRef = useRef(false)
  const previousControlsEnabledRef = useRef<boolean | null>(null)

  const [draggingPlaneId, setDraggingPlaneId] = useState<string | null>(null)
  const [hoveredPlaneId, setHoveredPlaneId] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (previousControlsEnabledRef.current !== null && controls) {
        controls.enabled = previousControlsEnabledRef.current
        previousControlsEnabledRef.current = null
      }
    }
  }, [controls])

  useEffect(() => {
    previousClippingPlanesRef.current = gl.clippingPlanes
    previousLocalClippingEnabledRef.current = gl.localClippingEnabled
    gl.localClippingEnabled = true

    return () => {
      gl.clippingPlanes = previousClippingPlanesRef.current || []
      gl.localClippingEnabled = previousLocalClippingEnabledRef.current
    }
  }, [gl])

  const resolvedClipPlanes = useMemo<ResolvedClipPlane[]>(
    () =>
      clipPlanes.map(clipPlane => {
        const basePoint = clipPlane.point
          .clone()
          .add(clipPlane.normal.clone().multiplyScalar(clipPlane.offset))

        const controllerPlane = new ThreePlane().setFromNormalAndCoplanarPoint(
          clipPlane.normal.clone(),
          basePoint.clone(),
        )

        let clippingPlanes: ThreePlane[] = []

        if (clipPlane.mode === 'half-space') {
          const clippingPlane = controllerPlane.clone()
          if (clipPlane.isInverted) {
            clippingPlane.negate()
          }
          clippingPlanes = [clippingPlane]
        } else {
          const halfDistance = clipPlane.bracketDistance / 2
          const bracketPositivePoint = basePoint
            .clone()
            .add(clipPlane.normal.clone().multiplyScalar(halfDistance))
          const bracketNegativePoint = basePoint
            .clone()
            .add(clipPlane.normal.clone().multiplyScalar(-halfDistance))

          clippingPlanes = [
            new ThreePlane().setFromNormalAndCoplanarPoint(
              clipPlane.normal.clone().negate(),
              bracketPositivePoint,
            ),
            new ThreePlane().setFromNormalAndCoplanarPoint(
              clipPlane.normal.clone(),
              bracketNegativePoint,
            ),
          ]
        }

        const rotation = makeClipPlaneRotation(clipPlane.normal)
        const displayNormal =
          clipPlane.mode === 'half-space' && clippingPlanes[0]
            ? clippingPlanes[0].normal
            : controllerPlane.normal
        const controllerPosition = getClipPlaneCenterOnPlane(
          basePoint,
          clipPlane.normal,
          clipPlane.rectangle,
        ).add(displayNormal.clone().multiplyScalar(0.1))

        return {
          ...clipPlane,
          clippingPlanes,
          controllerPlane,

          controllerPosition,
          rotation,
          outlinePoints: makeClipPlaneOutlinePoints(
            clipPlane.rectangle.width,
            clipPlane.rectangle.height,
          ),
        }
      }),
    [clipPlanes],
  )

  useEffect(() => {
    const globalClipPlanes = resolvedClipPlanes
      .filter(clipPlane => clipPlane.target === 'all')
      .flatMap(clipPlane => clipPlane.clippingPlanes)

    gl.clippingPlanes = applyClipPlanes ? globalClipPlanes : []
  }, [applyClipPlanes, gl, resolvedClipPlanes])

  useEffect(() => {
    setClipPlanes([])
    setDraggingPlaneId(null)
    setHoveredPlaneId(null)
  }, [clipPlaneResetVersion])

  const createClipPlane = useCallback(
    (
      normal: Vector3,
      point: Vector3,
      mode: ClipPlaneMode = 'half-space',
      target: ClipPlaneConfig['target'] = 'all',
      options: Pick<ClipPlaneConfig, 'linkGroupId'> &
        Partial<Pick<ClipPlaneConfig, 'renderMode' | 'id'>> & { replaceExisting?: boolean } = {},
    ) => {
      const normalizedNormal = normal.clone().normalize()
      const rectangle = createClipPlaneRectangle(
        scene,
        normalizedNormal,
        point,
        clipPlaneSizeFromScene,
      )

      const newClipPlane: ClipPlaneConfig = {
        id: options.id ?? `clip-plane-${nextPlaneIdRef.current++}`,
        normal: normalizedNormal,
        point: point.clone(),
        offset: 0,
        isInverted: false,
        rectangle,
        target,
        linkGroupId: options.linkGroupId,
        renderMode: options.renderMode ?? DEFAULT_CLIP_PLANE_RENDER_MODE,
        mode,
        bracketDistance: DEFAULT_BRACKET_MODE_DISTANCE,
      }

      setApplyClipPlanes(true)
      setClipPlanes(current =>
        options.replaceExisting ? [newClipPlane] : [...current, newClipPlane],
      )
    },
    [clipPlaneSizeFromScene, scene, setApplyClipPlanes],
  )

  useEffect(() => {
    const handleCreateClipPlane = (event: ClipPlaneCreateEvent) => {
      const { normal, point, target, linkGroupId, renderMode, mode, replaceExisting, id } =
        event.detail
      createClipPlane(
        new Vector3(normal.x, normal.y, normal.z),
        new Vector3(point.x, point.y, point.z),
        mode ?? 'half-space',
        target ?? 'all',
        {
          linkGroupId,
          renderMode,
          replaceExisting,
          id,
        },
      )
    }

    subscribeClipPlaneCreateEvent(handleCreateClipPlane)
    return () => {
      unsubscribeClipPlaneCreateEvent(handleCreateClipPlane)
    }
  }, [createClipPlane])

  useEffect(() => {
    setGltfClipPlanes(
      applyClipPlanes
        ? resolvedClipPlanes
            .filter(clipPlane => clipPlane.target === 'gltf')
            .flatMap(clipPlane => clipPlane.clippingPlanes)
        : [],
    )
  }, [applyClipPlanes, resolvedClipPlanes, setGltfClipPlanes])

  // Expose the ids of the currently existing clip planes so panels can react to their removal.
  useEffect(() => {
    setClipPlaneIds(clipPlanes.map(clipPlane => clipPlane.id))
  }, [clipPlanes, setClipPlaneIds])

  const createClipPlaneFromPoints = useCallback(
    (points: Vector3[]) => {
      if (points.length !== 3) return false

      const normal = points[1].clone().sub(points[0]).cross(points[2].clone().sub(points[0]))

      if (normal.lengthSq() <= 1e-6) {
        return false
      }

      createClipPlane(normal.normalize(), points[0])

      return true
    },
    [createClipPlane],
  )

  const handleClipPlaneDrawEnd = useCallback(
    (state: TransientDrawState) => {
      if (!state.drawPoint) return

      const point = state.drawPoint.v.clone()
      const nextPoints = [...pendingClipPlanePointsRef.current, point]

      if (nextPoints.length < 3) {
        setPendingClipPlanePoints(nextPoints)
        return
      }

      setPendingClipPlanePoints([])

      if (createClipPlaneFromPoints(nextPoints)) {
        unsetSelectionMode()
      }
    },
    [
      createClipPlaneFromPoints,
      pendingClipPlanePointsRef,
      setPendingClipPlanePoints,
      unsetSelectionMode,
    ],
  )

  useSceneContextMenuAction({
    label: 'Schnittfläche umkehren',
    action: (_selectedElement, _point, _event, _userDataActions, userData) => {
      const planeId = userData?.planeId

      if (!planeId) return

      setClipPlanes(current =>
        current.map(clipPlane =>
          clipPlane.id === planeId
            ? { ...clipPlane, isInverted: !clipPlane.isInverted }
            : clipPlane,
        ),
      )
    },
    available: (_selectedElement, point, _userDataActions, userData) =>
      !!point &&
      userData.clipController === true &&
      userData.mode === 'half-space' &&
      !!userData.planeId,
    group: 'clip-controller',
  })

  useSceneContextMenuAction({
    label: 'Schnittfläche zu Klammermodus ändern',
    action: (_selectedElement, _point, _event, _userDataActions, userData) => {
      const planeId = userData?.planeId

      if (!planeId) return

      setClipPlanes(current =>
        current.map(clipPlane =>
          clipPlane.id === planeId
            ? { ...clipPlane, mode: 'bracket', isInverted: false }
            : clipPlane,
        ),
      )
    },
    available: (_selectedElement, point, _userDataActions, userData) =>
      !!point &&
      userData.clipController === true &&
      userData.mode === 'half-space' &&
      !!userData.planeId,
    group: 'clip-controller',
  })

  useSceneContextMenuAction({
    label: 'Schnittfläche zu Halbebenenmodus ändern',
    action: (_selectedElement, _point, _event, _userDataActions, userData) => {
      const planeId = userData?.planeId

      if (!planeId) return

      setClipPlanes(current =>
        current.map(clipPlane =>
          clipPlane.id === planeId ? { ...clipPlane, mode: 'half-space' } : clipPlane,
        ),
      )
    },
    available: (_selectedElement, point, _userDataActions, userData) =>
      !!point &&
      userData.clipController === true &&
      userData.mode === 'bracket' &&
      !!userData.planeId,
    group: 'clip-controller',
  })

  const renderBracketDistanceField = useCallback(
    ({ userData }: SceneContextMenuActionRenderProps) => {
      const planeId = userData?.planeId as string | undefined

      if (!planeId) return null

      const clipPlane = clipPlanesRef.current.find(plane => plane.id === planeId)

      if (!clipPlane) return null

      return (
        <ThemeProvider theme={theme}>
          <BracketDistanceField
            initialValue={clipPlane.bracketDistance}
            onCommit={value =>
              setClipPlanes(current =>
                current.map(plane =>
                  plane.id === planeId ? { ...plane, bracketDistance: value } : plane,
                ),
              )
            }
          />
        </ThemeProvider>
      )
    },
    [clipPlanesRef, theme],
  )

  const availableBracketDistanceField = useCallback(
    (
      _selectedElement: string | null,
      point: Vector3 | null,
      _userDataActions: Record<string, unknown>,
      userData: Record<string, unknown>,
    ) =>
      !!point &&
      userData.clipController === true &&
      userData.mode === 'bracket' &&
      !!userData.planeId,
    [],
  )

  useSceneContextMenuAction({
    render: renderBracketDistanceField,
    available: availableBracketDistanceField,
    group: 'clip-controller',
  })

  const deleteClipPlaneAction = useCallback(
    (
      _selectedElement: string | null,
      _point: Vector3 | null,
      _event: React.MouseEvent<Element, MouseEvent>,
      userDataActions: Record<string, unknown>,
    ) => {
      const actionPayload = toClipPlaneActionPayload(userDataActions.deleteClipping)

      if (!actionPayload?.planeId) return

      const deletedPlane = clipPlanesRef.current.find(
        clipPlane => clipPlane.id === actionPayload.planeId,
      )
      const shouldClearPlaneState = (planeId: string) => {
        if (planeId === actionPayload.planeId) return true
        if (!deletedPlane?.linkGroupId) return false

        return clipPlanesRef.current.some(
          clipPlane =>
            clipPlane.id === planeId && clipPlane.linkGroupId === deletedPlane.linkGroupId,
        )
      }

      const linkGroupId = deletedPlane?.linkGroupId
      setClipPlanes(current =>
        current.filter(clipPlane =>
          linkGroupId
            ? clipPlane.linkGroupId !== linkGroupId
            : clipPlane.id !== actionPayload.planeId,
        ),
      )
      setHoveredPlaneId(current => (current && shouldClearPlaneState(current) ? null : current))
      setDraggingPlaneId(current => (current && shouldClearPlaneState(current) ? null : current))
    },
    [clipPlanesRef, setClipPlanes, setHoveredPlaneId, setDraggingPlaneId],
  )

  const availableDeleteClipPlaneAction = useCallback(
    (
      _selectedElement: string | null,
      point: Vector3 | null,
      userDataActions: Record<string, unknown>,
      userData: Record<string, unknown>,
    ) =>
      !!point &&
      userData.clipController === true &&
      !!toClipPlaneActionPayload(userDataActions.deleteClipping)?.planeId,
    [],
  )

  useSceneContextMenuAction({
    label: 'Schnittfläche löschen',
    action: deleteClipPlaneAction,
    available: availableDeleteClipPlaneAction,
    group: 'clip-controller',
  })

  const createClipPlaneAction = useCallback(
    (
      _selectedElement: string | null,
      _point: Vector3 | null,
      _event: React.MouseEvent<Element, MouseEvent>,
      userDataActions: Record<string, unknown>,
    ) => {
      const actionPayload = toClipPlaneActionPayload(userDataActions['clip-plane'])
      const normal = toVector3(actionPayload?.normal)
      const point = toVector3(actionPayload?.point)

      if (!normal || !point) return

      createClipPlane(normal, point, 'half-space')
    },
    [createClipPlane],
  )

  const availableCreateClipPlaneAction = useCallback(
    (
      _selectedElement: string | null,
      _point: Vector3 | null,
      userDataActions: Record<string, unknown>,
    ) => 'clip-plane' in userDataActions,
    [],
  )

  useSceneContextMenuAction({
    label: 'Schnittfläche erstellen',
    action: createClipPlaneAction,
    available: availableCreateClipPlaneAction,
    group: 'clip-controller',
  })

  const createBracketClipPlaneAction = useCallback(
    (
      _selectedElement: string | null,
      _point: Vector3 | null,
      _event: React.MouseEvent<Element, MouseEvent>,
      userDataActions: Record<string, unknown>,
    ) => {
      const actionPayload = toClipPlaneActionPayload(userDataActions['clip-plane'])
      const normal = toVector3(actionPayload?.normal)
      const point = toVector3(actionPayload?.point)

      if (!normal || !point) return

      createClipPlane(normal, point, 'bracket')
    },
    [createClipPlane],
  )

  const availableCreateBracketClipPlaneAction = useCallback(
    (
      _selectedElement: string | null,
      _point: Vector3 | null,
      userDataActions: Record<string, unknown>,
    ) => 'clip-plane' in userDataActions,
    [],
  )

  useSceneContextMenuAction({
    label: 'Schnittklammer erstellen (1m)',
    action: createBracketClipPlaneAction,
    available: availableCreateBracketClipPlaneAction,
    group: 'clip-controller',
  })

  const getPointerCaptureTarget = (
    event: ThreeEvent<PointerEvent>,
  ): PointerCaptureTarget | undefined => {
    const target = event.target

    if (target && 'setPointerCapture' in target && 'releasePointerCapture' in target) {
      return target as PointerCaptureTarget
    }
  }

  const handleContextMenu = (
    planeId: string,
    mode: ClipPlaneMode,
    isInverted: boolean,
    event: ThreeEvent<MouseEvent | PointerEvent>,
  ) => {
    if (event.button !== 2 || mouseMoved.current) return

    event.stopPropagation()
    event.nativeEvent.preventDefault()
    openContextMenu(
      event.point.clone(),
      null,
      {
        invertClipping: {
          planeId,
          inverted: isInverted,
        },
        deleteClipping: {
          planeId,
        },
      },
      {
        clipController: true,
        mode,
        planeId,
      },
    )
  }

  const endDrag = (event: ThreeEvent<PointerEvent>) => {
    const dragState = dragStateRef.current
    const pointerTarget = getPointerCaptureTarget(event)

    if (!dragState || dragState.pointerId !== event.pointerId) return

    event.stopPropagation()
    pointerTarget?.releasePointerCapture(event.pointerId)

    dragStateRef.current = null
    if (previousControlsEnabledRef.current !== null && controls) {
      controls.enabled = previousControlsEnabledRef.current
      previousControlsEnabledRef.current = null
    }
    setDraggingPlaneId(null)
    setHoveredPlaneId(null)
  }

  const handlePointerDown = (clipPlaneId: string, event: ThreeEvent<PointerEvent>) => {
    const clipPlane = clipPlanes.find(currentClipPlane => currentClipPlane.id === clipPlaneId)
    const resolvedClipPlane = resolvedClipPlanes.find(
      currentClipPlane => currentClipPlane.id === clipPlaneId,
    )

    if (!clipPlane || !resolvedClipPlane) return

    const pointerTarget = getPointerCaptureTarget(event)

    if (event.button !== 0) return

    event.stopPropagation()
    pointerTarget?.setPointerCapture(event.pointerId)

    const dragAxis = (
      resolvedClipPlane.mode === 'half-space' && resolvedClipPlane.clippingPlanes[0]
        ? resolvedClipPlane.clippingPlanes[0].normal
        : resolvedClipPlane.controllerPlane.normal
    )
      .clone()
      .normalize()
    const dragPlaneNormal = makeDragPlaneNormal(dragAxis, camera.getWorldDirection(new Vector3()))
    const offsetDirection = Math.sign(dragAxis.dot(clipPlane.normal)) || 1
    const linkedStartOffsets = clipPlanes.reduce<Record<string, number>>(
      (acc, currentClipPlane) => {
        if (
          currentClipPlane.id === clipPlane.id ||
          (clipPlane.linkGroupId && currentClipPlane.linkGroupId === clipPlane.linkGroupId)
        ) {
          acc[currentClipPlane.id] = currentClipPlane.offset
        }

        return acc
      },
      {},
    )

    dragStateRef.current = {
      planeId: clipPlane.id,
      dragPlane: new ThreePlane().setFromNormalAndCoplanarPoint(
        dragPlaneNormal,
        event.point.clone(),
      ),
      dragAxis,
      offsetDirection,
      pointerId: event.pointerId,
      startOffset: clipPlane.offset,
      linkedStartOffsets,
      startPoint: event.point.clone(),
    }

    if (controls) {
      previousControlsEnabledRef.current = controls.enabled
      controls.enabled = false
    }
    setDraggingPlaneId(clipPlane.id)
  }

  const handlePointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const dragState = dragStateRef.current

      if (!dragState || dragState.pointerId !== event.pointerId) return

      event.stopPropagation()

      const intersection = new Vector3()
      const intersectsDragPlane = event.ray.intersectPlane(dragState.dragPlane, intersection)

      if (!intersectsDragPlane) return

      const offsetDelta =
        intersection.sub(dragState.startPoint).dot(dragState.dragAxis) * dragState.offsetDirection

      const nextOffset = dragState.startOffset + offsetDelta
      const currentPlane = clipPlanesRef.current.find(
        clipPlane => clipPlane.id === dragState.planeId,
      )
      if (currentPlane && Math.abs(nextOffset - currentPlane.offset) < 0.01) return

      setClipPlanes(current =>
        current.map(clipPlane => {
          const linkedStartOffset = dragState.linkedStartOffsets[clipPlane.id]

          return typeof linkedStartOffset === 'number'
            ? { ...clipPlane, offset: linkedStartOffset + offsetDelta }
            : clipPlane
        }),
      )
    },
    [clipPlanesRef, setClipPlanes],
  )

  return (
    <>
      {isPickingClipPlanePoints && (
        <DrawControllerWrapper
          enabled={clipPlaneDrawEnabled}
          enableIndicator
          xyOnly={isOrthographic}
          color={theme.scenePalette.highlightDark as string}
          onDrawEnd={handleClipPlaneDrawEnd}
        />
      )}
      {pendingClipPlanePoints.map((point, index) => (
        <ScalingIndicator
          key={`clip-plane-point-${index}`}
          position={point}
          color={theme.scenePalette.highlightDark as string}
          visible
        />
      ))}
      {applyClipPlanes &&
        resolvedClipPlanes.map(clipPlane => {
          const isDragging = draggingPlaneId === clipPlane.id
          const isHovered = hoveredPlaneId === clipPlane.id
          const isOutlineOnly = clipPlane.renderMode === 'dashed-outline'

          return (
            <group
              key={clipPlane.id}
              position={clipPlane.controllerPosition}
              rotation={clipPlane.rotation}
            >
              {!isOutlineOnly && (
                <mesh
                  name={`clip-controller-plane-${clipPlane.id}`}
                  renderOrder={100}
                  layers={clipPlanesInteractive ? 0 : sceneConfig.R3FNonSelectableObjectLayer}
                  onPointerDown={event => handlePointerDown(clipPlane.id, event)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={event => {
                    handleContextMenu(clipPlane.id, clipPlane.mode, clipPlane.isInverted, event)
                    endDrag(event)
                  }}
                  onPointerCancel={endDrag}
                  onPointerEnter={() => setHoveredPlaneId(clipPlane.id)}
                  onPointerLeave={() => {
                    if (!isDragging) {
                      setHoveredPlaneId(current => (current === clipPlane.id ? null : current))
                    }
                  }}
                >
                  <planeGeometry args={[clipPlane.rectangle.width, clipPlane.rectangle.height]} />
                  <meshStandardMaterial
                    color={theme.scenePalette.helpers as string}
                    side={DoubleSide}
                    transparent
                    opacity={isDragging ? 0.25 : isHovered ? 0.18 : 0.12}
                    depthWrite={false}
                  />
                </mesh>
              )}
              <Line
                points={clipPlane.outlinePoints}
                layers={clipPlanesInteractive ? 0 : sceneConfig.R3FNonSelectableObjectLayer}
                color={
                  (isDragging || isHovered
                    ? theme.scenePalette.highlightDark
                    : theme.scenePalette.outlinesDark) as string
                }
                lineWidth={isDragging ? 2 : 1}
                dashed={isOutlineOnly}
                dashSize={0.6}
                gapSize={0.35}
                renderOrder={101}
                onPointerDown={
                  isOutlineOnly ? event => handlePointerDown(clipPlane.id, event) : undefined
                }
                onPointerMove={isOutlineOnly ? handlePointerMove : undefined}
                onPointerUp={
                  isOutlineOnly
                    ? event => {
                        handleContextMenu(clipPlane.id, clipPlane.mode, clipPlane.isInverted, event)
                        endDrag(event)
                      }
                    : undefined
                }
                onPointerCancel={isOutlineOnly ? endDrag : undefined}
                onPointerEnter={isOutlineOnly ? () => setHoveredPlaneId(clipPlane.id) : undefined}
                onPointerLeave={
                  isOutlineOnly
                    ? () => {
                        if (!isDragging) {
                          setHoveredPlaneId(current => (current === clipPlane.id ? null : current))
                        }
                      }
                    : undefined
                }
              />
            </group>
          )
        })}
    </>
  )
}

export default ClipController
